import { COLORS } from './constants';

const C = { ...COLORS, white: [255, 255, 255] };
const NBSP = '\u00A0';

const fmtEur = (v) => {
  if (v == null || isNaN(v)) return '-';
  const parts = Number(v).toFixed(2).split('.');
  const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
  return `${intPart},${parts[1]}${NBSP}\u20AC`;
};

// Layout constants
const M = 18, W = 210, H = 297, RIGHT = W - M, CW = RIGHT - M, FOOTER_Y = H - 12;

let fontFamily = 'helvetica';

// ── Shared helpers ───────────────────────────────────────────────────────
let cachedLogo = null;
const loadLogo = async () => {
  if (cachedLogo) return cachedLogo;
  try {
    const res = await fetch('/logo-pde-2025.png');
    const blob = await res.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => { cachedLogo = reader.result; resolve(cachedLogo); };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch { return null; }
};

const setupFont = async (doc) => {
  try {
    const { karlaRegular, karlaBold } = await import('../../fonts/karlaFont');
    if (karlaRegular) { doc.addFileToVFS('Karla-Regular.ttf', karlaRegular); doc.addFont('Karla-Regular.ttf', 'Karla', 'normal'); }
    if (karlaBold) { doc.addFileToVFS('Karla-Bold.ttf', karlaBold); doc.addFont('Karla-Bold.ttf', 'Karla', 'bold'); }
    if (doc.getFontList()['Karla']) fontFamily = 'Karla';
  } catch { /* fallback */ }
  doc.setFont(fontFamily, 'normal');
};

const drawHeader = (doc, logo) => {
  if (logo) { try { doc.addImage(logo, 'PNG', M, 10, 35, 9, undefined, 'FAST'); } catch {} }
  doc.setDrawColor(...C.azur);
  doc.setLineWidth(0.8);
  doc.line(M, 22, RIGHT, 22);
  doc.setTextColor(...C.anthracite);
  doc.setFontSize(11);
  doc.setFont(fontFamily, 'bold');
  doc.text("RAPPORT D'\u00C9LIGIBILIT\u00C9 AU TAUX R\u00C9DUIT DE TICFE", W / 2, 29, { align: 'center' });
};

const drawFooter = (doc, pg, total, contact) => {
  doc.setDrawColor(...C.argent);
  doc.setLineWidth(0.25);
  doc.line(M, FOOTER_Y - 4, RIGHT, FOOTER_Y - 4);
  doc.setTextColor(...C.grisAcier);
  doc.setFontSize(7);
  doc.setFont(fontFamily, 'normal');
  const parts = [];
  if (contact?.name) parts.push(contact.name);
  if (contact?.phone) parts.push(contact.phone);
  if (contact?.email) parts.push(contact.email);
  doc.text(parts.length ? parts.join(' - ') : 'contact : energymanager@cockpit.placedesenergies.com', M, FOOTER_Y);
  doc.text(`${pg} / ${total}`, RIGHT, FOOTER_Y, { align: 'right' });
};

const secTitle = (doc, text, y) => {
  doc.setTextColor(...C.anthracite);
  doc.setFontSize(11);
  doc.setFont(fontFamily, 'bold');
  doc.text(text, M, y);
  doc.setDrawColor(...C.azur);
  doc.setLineWidth(0.8);
  doc.line(M, y + 1.5, M + doc.getTextWidth(text), y + 1.5);
  return y + 10;
};

const para = (doc, text, y, maxW) => {
  if (!text) return y;
  doc.setTextColor(...C.bleuPetroleDark);
  doc.setFontSize(9);
  doc.setFont(fontFamily, 'normal');
  const lines = doc.splitTextToSize(text, maxW || CW);
  doc.text(lines, M, y);
  return y + lines.length * 3.8 + 3;
};

const subTitle = (doc, text, y) => {
  doc.setTextColor(...C.anthracite);
  doc.setFontSize(10);
  doc.setFont(fontFamily, 'bold');
  doc.text(text, M, y);
  return y + 7;
};

const labelVal = (doc, label, value, x, y) => {
  doc.setTextColor(...C.grisAcier);
  doc.setFontSize(7.5);
  doc.setFont(fontFamily, 'normal');
  doc.text(label, x, y);
  doc.setTextColor(...C.anthracite);
  doc.setFontSize(9);
  doc.setFont(fontFamily, 'bold');
  doc.text(String(value || '-'), x, y + 4.5);
};

// ══════════════════════════════════════════════════════════════════════════
// MAIN EXPORT
// ══════════════════════════════════════════════════════════════════════════
export const generateTicfePDF = async (report) => {
  const { jsPDF } = await import('jspdf');
  const autoTableModule = await import('jspdf-autotable');
  const autoTable = autoTableModule.default;

  const doc = new jsPDF('p', 'mm', 'a4');
  await setupFont(doc);
  const logo = await loadLogo();

  // ═══════════════════════════════════════════════════════════════════════
  // PAGE 1 — Introduction
  // ═══════════════════════════════════════════════════════════════════════
  drawHeader(doc, logo);
  let y = 38;

  // Client info card
  doc.setFillColor(...C.blancLunaire);
  doc.roundedRect(M, y, CW, 32, 2, 2, 'F');
  labelVal(doc, "Charg\u00E9 d'affaires PDE :", report.contact?.name || '-', M + 4, y + 5);
  labelVal(doc, 'Client :', report.client_name || '-', M + 70, y + 5);
  labelVal(doc, 'PDL :', report.pdl || '-', M + 4, y + 17);
  const addr = report.address
    ? `${report.address.street || ''}, ${report.address.postal_code || ''} ${report.address.city || ''}`
    : '-';
  labelVal(doc, 'Adresse :', addr, M + 70, y + 17);
  if (report.upload_date) {
    const d = new Date(report.upload_date);
    doc.setTextColor(...C.grisAcier);
    doc.setFontSize(7);
    doc.setFont(fontFamily, 'normal');
    doc.text(`Date : ${d.toLocaleDateString('fr-FR')}`, RIGHT - 4, y + 6, { align: 'right' });
  }
  y += 38;

  // Section: Qu'est-ce que la TICFE ?
  y = secTitle(doc, "Qu'est-ce que la TICFE ?", y);
  y = para(doc,
    "Autrement appel\u00E9e Accise sur l'\u00C9nergie ou CSPE (Contribution au Service Public d'\u00C9lectricit\u00E9), " +
    "la Taxe Int\u00E9rieure sur la Consommation Finale d'\u00C9lectricit\u00E9 est due par tous les consommateurs " +
    "d'\u00E9lectricit\u00E9 au prorata de leur consommation. Il s'agit d'une taxe figurant sur la facture " +
    "d'\u00E9lectricit\u00E9. Bien que cette taxe soit g\u00E9r\u00E9e, recouvr\u00E9e et contr\u00F4l\u00E9e par " +
    "l'administration fiscale depuis 2022, ce sont les fournisseurs d'\u00E9lectricit\u00E9 qui sont en charge " +
    "de percevoir les montants et de les restituer \u00E0 l'administration fiscale ensuite.", y);

  y += 2;
  y = secTitle(doc, "Quels tarifs sont applicables en 2026 ?", y);
  y += 1;

  // 2026 Prices table
  autoTable(doc, {
    startY: y,
    head: [['Cat\u00E9gorie fiscale', 'En janvier', '\u00C0 partir du\n1er f\u00E9vrier']],
    body: [
      ['M\u00E9nages et assimil\u00E9s', '29,98 \u20AC', '30,85 \u20AC'],
      ['PME (36-250 kVA)', '25,79 \u20AC', '26,58 \u20AC'],
      ['Haute puissance (>250 kVA)', '25,79 \u20AC', '26,58 \u20AC'],
    ],
    theme: 'plain',
    styles: { fontSize: 8.5, cellPadding: 3, font: fontFamily, halign: 'center' },
    headStyles: { fillColor: C.azur, textColor: C.white, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: C.blancLunaire },
    columnStyles: { 0: { halign: 'left', fontStyle: 'bold' } },
    margin: { left: M, right: M },
  });
  y = doc.lastAutoTable.finalY + 8;

  // Section: Éligibilité
  y = secTitle(doc, "Qui est \u00E9ligible au tarif r\u00E9duit de TICFE ?", y);
  y = para(doc,
    "La r\u00E9glementation pr\u00E9voit des tarifs r\u00E9duits en faveur des entreprises dont l'\u00E9lectricit\u00E9 " +
    "est consomm\u00E9e pour les besoins des :", y);

  const eligCategories = [
    "Entreprises industrielles \u00E9lectro-intensives",
    "Entreprises dont l'activit\u00E9 est expos\u00E9e \u00E0 un risque important de fuite de carbone",
    "Entreprises hyper \u00E9lectro-intensives",
    "Entreprises pour lesquelles le co\u00FBt de l'\u00E9lectricit\u00E9 repr\u00E9sente au moins la moiti\u00E9 du co\u00FBt de production d'un produit",
  ];
  eligCategories.forEach(cat => {
    doc.setFillColor(...C.azur);
    doc.circle(M + 2, y + 1, 0.7, 'F');
    doc.setTextColor(...C.bleuPetroleDark);
    doc.setFontSize(9);
    doc.setFont(fontFamily, 'normal');
    doc.text(cat, M + 6, y + 2);
    y += 5;
  });

  // ═══════════════════════════════════════════════════════════════════════
  // PAGE 2 — Votre situation
  // ═══════════════════════════════════════════════════════════════════════
  doc.addPage();
  drawHeader(doc, logo);
  y = 38;

  y = secTitle(doc, "Votre situation :", y);

  // Key-value data
  const kvData = [
    ['N\u00B0 de PDL', report.pdl || '-'],
    ['Code APE', `${report.ape_code || '-'} - ${report.ape_description || ''}`],
    ['Valeur Ajout\u00E9e', report.added_value != null ? `${Number(report.added_value).toLocaleString('fr-FR')}` : '-'],
    ['P\u00E9riode Concern\u00E9e', `${report.period_start || '-'}  \u2192  ${report.period_end || '-'}`],
    ['Consommation sur la p\u00E9riode (kWh)', report.consumption_mwh != null ? `${(Number(report.consumption_mwh) * 1000).toLocaleString('fr-FR')}` : '-'],
    ['Montant actuel de la TICFE', report.current_ticfe_amount != null ? fmtEur(report.current_ticfe_amount) : '-'],
  ];

  kvData.forEach(([label, value]) => {
    doc.setTextColor(...C.grisAcier);
    doc.setFontSize(9);
    doc.setFont(fontFamily, 'normal');
    doc.text(label + ' :', M, y);
    doc.setTextColor(...C.anthracite);
    doc.setFont(fontFamily, 'bold');
    doc.text(String(value), M + 75, y);
    y += 7;
  });

  // Eligibility badge
  y += 4;
  const isEligible = (report.eligibility || '').toLowerCase().includes('ligible') && !(report.eligibility || '').toLowerCase().includes('non');
  if (isEligible) {
    doc.setFillColor(...C.emeraude);
  } else {
    doc.setFillColor(220, 50, 50);
  }
  doc.roundedRect(M, y, CW, 14, 3, 3, 'F');
  doc.setTextColor(...C.white);
  doc.setFontSize(11);
  doc.setFont(fontFamily, 'bold');
  doc.text(`\u00C9ligibilit\u00E9 au taux r\u00E9duit : ${report.eligibility || '-'}`, W / 2, y + 9, { align: 'center' });
  y += 22;

  // Recovery methods
  y = secTitle(doc, "Les 2 m\u00E9thodes de r\u00E9cup\u00E9ration de la TICFE :", y);

  y = subTitle(doc, "Le syst\u00E8me de l'attestation :", y);
  y = para(doc,
    "Le syst\u00E8me de l'attestation permet \u00E0 une entreprise de ne plus \u00EAtre factur\u00E9e de la TICFE " +
    "\u00E0 taux plein par son fournisseur pour les factures \u00E0 venir tout au long de son contrat. " +
    "Il reste n\u00E9cessaire de v\u00E9rifier l'\u00E9ligibilit\u00E9 en r\u00E9alisant un bilan \u00E9nerg\u00E9tique " +
    "dans les 6 mois qui suivent la cl\u00F4ture de l'exercice fiscal. Il s'agit d'une d\u00E9claration qui doit " +
    "\u00EAtre v\u00E9rifi\u00E9e au pr\u00E9alable afin de ne pas prendre le risque d'un redressement fiscal.", y);

  y += 3;
  y = subTitle(doc, "Le syst\u00E8me du remboursement :", y);
  y = para(doc,
    "Le syst\u00E8me du remboursement a l'avantage de ne repr\u00E9senter aucun risque de redressement fiscal. " +
    "L'entreprise dispose d'un d\u00E9lai de 2 ann\u00E9es plus l'ann\u00E9e en cours pour formuler ses demandes " +
    "de remboursement. Cela n\u00E9cessite en revanche de disposer de l'ensemble des factures d'\u00E9lectricit\u00E9 " +
    "et des liasses fiscales approuv\u00E9es par un expert comptable.", y);

  // ═══════════════════════════════════════════════════════════════════════
  // PAGE 3 — Économies potentielles
  // ═══════════════════════════════════════════════════════════════════════
  doc.addPage();
  drawHeader(doc, logo);
  y = 38;

  y = secTitle(doc, "Vos \u00E9conomies potentielles :", y);

  // Attestation savings
  y = subTitle(doc, "Par le syst\u00E8me d'attestation :", y);
  y = para(doc,
    "En se basant sur les consommations de la p\u00E9riode de r\u00E9f\u00E9rence, nous projetons votre " +
    "consommation sur les 12 prochains mois.", y);

  // Savings highlight box
  const attestSavings = report.savings?.attestation;
  y += 2;
  doc.setFillColor(...C.emeraude);
  doc.roundedRect(M, y, CW, 14, 3, 3, 'F');
  doc.setTextColor(...C.white);
  doc.setFontSize(11);
  doc.setFont(fontFamily, 'bold');
  doc.text(`\u00C9conomies sur les 12 prochains mois : ${attestSavings != null ? fmtEur(attestSavings) : '-'}`, W / 2, y + 9, { align: 'center' });
  y += 22;

  // Rappel
  doc.setTextColor(...C.anthracite);
  doc.setFontSize(10);
  doc.setFont(fontFamily, 'bold');
  doc.text('Rappel :', M, y);
  y += 5;
  y = para(doc,
    "Ce syst\u00E8me d'attestation est valable pour la dur\u00E9e du contrat actuel, mais il est recommand\u00E9 " +
    "de v\u00E9rifier l'\u00E9ligibilit\u00E9 chaque ann\u00E9e. Il reste n\u00E9cessaire de r\u00E9aliser un bilan " +
    "\u00E9nerg\u00E9tique au plus tard le 6\u00E8me mois qui suit la cl\u00F4ture de son exercice fiscal.", y);

  y += 6;
  // Reimbursement savings
  y = subTitle(doc, "Par le syst\u00E8me de remboursement :", y);
  y = para(doc,
    "Gr\u00E2ce \u00E0 vos consommations historiques, nous pouvons calculer le remboursement par " +
    "l'administration fiscale sur les derni\u00E8res ann\u00E9es.", y);

  y += 2;
  doc.setTextColor(...C.anthracite);
  doc.setFontSize(10);
  doc.setFont(fontFamily, 'bold');
  doc.text("\u00C9conomies sur les derni\u00E8res ann\u00E9es :", M, y);
  y += 5;

  // Reimbursement table
  const reimb = report.savings?.reimbursement || {};
  const reimbRows = Object.entries(reimb)
    .map(([key, val]) => {
      const year = key.replace('year_', '');
      return [year, fmtEur(val)];
    })
    .sort((a, b) => a[0].localeCompare(b[0]));

  if (reimbRows.length) {
    autoTable(doc, {
      startY: y,
      head: [['Ann\u00E9e', 'Montant']],
      body: reimbRows,
      theme: 'plain',
      styles: { fontSize: 10, cellPadding: 4, font: fontFamily },
      headStyles: { fillColor: C.azur, textColor: C.white, fontStyle: 'bold' },
      bodyStyles: { fillColor: C.blancLunaire },
      columnStyles: { 0: { halign: 'center', cellWidth: 50 }, 1: { halign: 'center', fontStyle: 'bold', cellWidth: 60 } },
      margin: { left: M + 20, right: M + 20 },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // Rappel remboursement
  doc.setTextColor(...C.anthracite);
  doc.setFontSize(10);
  doc.setFont(fontFamily, 'bold');
  doc.text('Rappel :', M, y);
  y += 5;
  y = para(doc,
    "Le syst\u00E8me du remboursement suppose de disposer de l'ensemble des factures d'\u00E9lectricit\u00E9 " +
    "et des liasses fiscales approuv\u00E9es par un commissaire aux comptes ou un expert comptable " +
    "correspondant \u00E0 la p\u00E9riode concern\u00E9e par la demande de remboursement.", y);

  // ── Total savings recap ──
  y += 4;
  const totalReimb = Object.values(reimb).reduce((sum, v) => sum + (Number(v) || 0), 0);
  const totalSavings = (Number(attestSavings) || 0) + totalReimb;

  doc.setFillColor(...C.anthracite);
  doc.roundedRect(M, y, CW, 36, 3, 3, 'F');

  doc.setTextColor(...C.white);
  doc.setFontSize(10);
  doc.setFont(fontFamily, 'bold');
  doc.text('R\u00C9CAPITULATIF DES \u00C9CONOMIES', W / 2, y + 8, { align: 'center' });

  doc.setFontSize(9);
  doc.setFont(fontFamily, 'normal');
  doc.text(`Attestation (12 prochains mois) :`, M + 8, y + 16);
  doc.text(`${attestSavings != null ? fmtEur(attestSavings) : '-'}`, RIGHT - 8, y + 16, { align: 'right' });

  doc.text(`Remboursement (ann\u00E9es pr\u00E9c\u00E9dentes) :`, M + 8, y + 22);
  doc.text(`${totalReimb > 0 ? fmtEur(totalReimb) : '-'}`, RIGHT - 8, y + 22, { align: 'right' });

  doc.setDrawColor(...C.white);
  doc.setLineWidth(0.3);
  doc.line(M + 8, y + 25, RIGHT - 8, y + 25);

  doc.setFontSize(11);
  doc.setFont(fontFamily, 'bold');
  doc.setTextColor(...C.emeraude);
  doc.text('TOTAL :', M + 8, y + 32);
  doc.text(`${fmtEur(totalSavings)}`, RIGHT - 8, y + 32, { align: 'right' });

  // ═══════════════════════════════════════════════════════════════════════
  // PAGE 4 — Conclusion & Responsabilités
  // ═══════════════════════════════════════════════════════════════════════
  doc.addPage();
  drawHeader(doc, logo);
  y = 38;

  y = secTitle(doc, 'CONCLUSION', y);
  y = para(doc,
    "Votre entreprise \u00E9tant bien \u00E9ligible \u00E0 un taux r\u00E9duit de la TICFE, Place Des Energies " +
    "vous propose de vous accompagner dans la r\u00E9alisation de vos demandes d'application du taux r\u00E9duit " +
    "ainsi que dans la demande de remboursement.", y);
  y = para(doc,
    "En contrepartie, Place Des Energies facture 30% du montant de l'\u00E9conomie r\u00E9alis\u00E9e. " +
    "Aucun co\u00FBt n'est factur\u00E9 si aucune \u00E9conomie n'est obtenue.", y);
  y = para(doc,
    "Il est possible de s\u00E9lectionner un seul syst\u00E8me d'application du taux r\u00E9duit " +
    "(sur attestation ou par remboursement), mais \u00E9galement de r\u00E9aliser une demande sur les 2 syst\u00E8mes, " +
    "permettant ainsi de maximiser les \u00E9conomies. Ce choix vous appartient.", y);

  y += 6;
  y = secTitle(doc, 'RESPONSABILIT\u00C9S', y);
  y = para(doc,
    "Place des \u00C9nergies agit en tant qu'interm\u00E9diaire ind\u00E9pendant dans le cadre de l'analyse " +
    "d'\u00E9ligibilit\u00E9 \u00E0 la r\u00E9duction de l'accise sur l'\u00E9lectricit\u00E9. Notre r\u00F4le est " +
    "d'accompagner l'entreprise dans la collecte, l'analyse et la transmission des informations n\u00E9cessaires " +
    "\u00E0 l'application d'un taux r\u00E9duit ou \u00E0 la demande de remboursement.", y);
  y = para(doc,
    "La responsabilit\u00E9 de Place des \u00C9nergies ne saurait \u00EAtre engag\u00E9e en cas de rejet de la " +
    "demande par l'administration fiscale, notamment en raison d'informations inexactes, incompl\u00E8tes ou " +
    "non transmises par l'entreprise, ou en cas de modification ult\u00E9rieure de la r\u00E9glementation.", y);
  y = para(doc,
    "Il incombe au client de valider les informations transmises et, le cas \u00E9ch\u00E9ant, de faire " +
    "v\u00E9rifier les dossiers par un expert-comptable ou un conseiller fiscal. Place des \u00C9nergies s'engage " +
    "\u00E0 agir avec diligence et loyaut\u00E9, dans la limite des informations communiqu\u00E9es.", y);

  // ═══════════════════════════════════════════════════════════════════════
  // PAGE 5 — Contact
  // ═══════════════════════════════════════════════════════════════════════
  doc.addPage();
  drawHeader(doc, logo);
  y = 38;

  y = secTitle(doc, 'Nous contacter :', y);
  y += 3;

  // Advisor card
  doc.setFillColor(...C.blancLunaire);
  doc.roundedRect(M, y, CW, 48, 3, 3, 'F');
  doc.setTextColor(...C.anthracite);
  doc.setFontSize(11);
  doc.setFont(fontFamily, 'bold');
  doc.text('Votre conseiller :', M + 8, y + 12);
  doc.setFontSize(10);
  doc.setTextColor(...C.bleuPetroleDark);
  let cy = y + 20;
  if (report.contact?.name) {
    doc.setFont(fontFamily, 'bold');
    doc.text(report.contact.name, M + 8, cy);
    doc.setFont(fontFamily, 'normal');
    cy += 6;
  }
  if (report.contact?.phone) { doc.text(`T\u00E9l\u00E9phone : ${report.contact.phone}`, M + 8, cy); cy += 6; }
  if (report.contact?.email) { doc.text(`E-mail : ${report.contact.email}`, M + 8, cy); cy += 6; }
  if (report.contact?.office_address) {
    const rawCity = (report.contact.office_city || '').replace(/[\n\r]/g, ' ').replace(/\s+/g, ' ').trim();
    const city = rawCity.replace(/\s+[A-Z]$/, '').trim();
    doc.text(`Adresse : ${report.contact.office_address}${city ? ', ' + city : ''}`, M + 8, cy);
  }
  y += 56;

  // Company card - Agency info
  doc.setFillColor(...C.blancLunaire);
  doc.roundedRect(M, y, CW, 30, 3, 3, 'F');
  doc.setTextColor(...C.anthracite);
  doc.setFontSize(11);
  doc.setFont(fontFamily, 'bold');
  doc.text('PLACE DES ENERGIES', M + 8, y + 12);
  doc.setFontSize(9);
  doc.setFont(fontFamily, 'normal');
  doc.setTextColor(...C.bleuPetroleDark);
  const agencyName = report.agency?.name;
  const agencyAddr = report.agency?.address || '33 Avenue Auguste Ferret';
  const agencyCity = report.agency?.city || '33110 LE BOUSCAT';
  if (agencyName) {
    doc.text(agencyName, M + 8, y + 20);
    doc.text(`${agencyAddr}, ${agencyCity}`, M + 8, y + 26);
  } else {
    doc.text(`Adresse : ${agencyAddr}, ${agencyCity}`, M + 8, y + 20);
    doc.text('www.placedesenergies.com', M + 8, y + 26);
  }

  // ═══════════════════════════════════════════════════════════════════════
  // Footers
  // ═══════════════════════════════════════════════════════════════════════
  const total = doc.getNumberOfPages();
  for (let p = 1; p <= total; p++) {
    doc.setPage(p);
    drawFooter(doc, p, total, report.contact);
  }

  const slug = (report.client_name || 'rapport').replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30);
  doc.save(`Rapport_TICFE_${slug}.pdf`);
};
